import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Vendor } from '@/types/vendor';
import {
  archiveVendor as archiveVendorApi,
  createVendor,
  deleteVendor,
  fetchVendors as fetchVendorsFromApi,
  unarchiveVendor as unarchiveVendorApi,
  updateVendor,
} from '@/services/vendorService';
import { extractErrorMessage } from '@/utils/apiError';
import { ArchivedVendorError, isArchived } from '@/utils/vendorArchive';

export const useVendorStore = defineStore('vendor', () => {
  const vendors = ref<Vendor[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function fetchVendors(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      vendors.value = await fetchVendorsFromApi();
    } catch (fetchError) {
      error.value = extractErrorMessage(fetchError);
    } finally {
      loading.value = false;
    }
  }

  const activeVendors = computed(() => vendors.value.filter((vendor) => !isArchived(vendor)));

  async function addVendor(odooVendorId: number, alias: string | null): Promise<Vendor> {
    const existing = vendors.value.find((v) => v.odooVendorId === odooVendorId);
    if (existing && isArchived(existing)) throw new ArchivedVendorError(existing);
    if (existing) return existing;

    const vendor = await createVendor(odooVendorId, alias);
    vendors.value.push(vendor);
    return vendor;
  }

  function replaceVendor(updated: Vendor): Vendor {
    const index = vendors.value.findIndex((v) => v.id === updated.id);
    if (index !== -1) vendors.value[index] = updated;
    return updated;
  }

  function isAdded(odooVendorId: number): boolean {
    return vendors.value.some((v) => v.odooVendorId === odooVendorId);
  }

  async function updateVendorAlias(id: string, alias: string | null): Promise<Vendor> {
    return replaceVendor(await updateVendor(id, alias));
  }

  async function archiveVendor(id: string): Promise<Vendor> {
    return replaceVendor(await archiveVendorApi(id));
  }

  async function unarchiveVendor(id: string): Promise<Vendor> {
    return replaceVendor(await unarchiveVendorApi(id));
  }

  async function removeVendor(id: string): Promise<void> {
    await deleteVendor(id);
    vendors.value = vendors.value.filter((v) => v.id !== id);
  }

  return {
    vendors,
    activeVendors,
    loading,
    error,
    fetchVendors,
    addVendor,
    isAdded,
    updateVendorAlias,
    archiveVendor,
    unarchiveVendor,
    removeVendor,
    replaceVendor,
  };
});
